import { draftApi } from '@/api/draftApi'
import { useAuthStore } from '@/stores/authStore'
import { useDraftStore } from '@/stores/draftStore'
import { usePresenceStore, type PresenceUser } from '@/stores/presenceStore'
import { useSelectionStore } from '@/stores/selectionStore'
import { applyDraftOps } from '@/services/realtime/ops'

type RealtimeEvent =
  | { type: 'draft.ops'; rev: number; ops: Array<Record<string, unknown>>; actor?: { userId?: number; name?: string } }
  | { type: 'presence.join' | 'presence.update' | 'presence.sync'; presence: PresenceUser }
  | { type: 'presence.leave'; presence: PresenceUser }

function buildWsUrl(projectId: number) {
  const explicit = (import.meta.env.VITE_WS_BASE_URL ?? '').replace(/\/+$/, '')
  if (explicit) return new URL(`/ws/projects/${projectId}/draft/`, `${explicit}/`).toString()

  const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '')
  const httpUrl = new URL(apiBase || '/', window.location.origin)
  httpUrl.protocol = httpUrl.protocol === 'https:' ? 'wss:' : 'ws:'
  httpUrl.pathname = `/ws/projects/${projectId}/draft/`
  httpUrl.search = ''
  return httpUrl.toString()
}

/**
 * Fecha uma lacuna de `rev` aplicando as ops perdidas. Devolve `false` quando
 * o servidor não consegue cobrir a lacuna inteira — aí só resta recarregar.
 */
async function catchUpMissedOps(projectId: number, since: number): Promise<boolean> {
  try {
    const response = await draftApi.changes(projectId, since)
    if (!response.complete || response.changes.length === 0) return false

    const last = response.changes[response.changes.length - 1]
    const ops = response.changes.flatMap((change) => change.ops)
    useDraftStore.getState().applyRealtimeOps(last.rev, ops, { name: last.author?.name })
    return true
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[realtime] failed to catch up missed ops', error)
    return false
  }
}

export function connectProjectRealtime(projectId: number) {
  let socket: WebSocket | null = null
  let reconnectTimer: number | null = null
  let heartbeatTimer: number | null = null
  let attempt = 0
  let closed = false

  const open = () => {
    usePresenceStore.getState().setStatus('connecting')
    try {
      socket = new WebSocket(buildWsUrl(projectId))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[realtime] failed to create websocket connection', error)
      usePresenceStore.getState().setStatus('disconnected')
      return
    }

    socket.onopen = () => {
      attempt = 0
      usePresenceStore.getState().setStatus('connected')
      sendPresence()
      heartbeatTimer = window.setInterval(() => sendPresence(), 15000)
    }

    socket.onmessage = async (event) => {
      let payload: RealtimeEvent
      try {
        payload = JSON.parse(event.data) as RealtimeEvent
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[realtime] invalid websocket payload', error)
        return
      }
      const currentUserId = useAuthStore.getState().user?.id ?? null

      if (payload.type === 'draft.ops') {
        const actorId = payload.actor?.userId ?? null
        if (actorId != null && actorId === currentUserId) return

        const state = useDraftStore.getState()
        if (!state.metadata || state.mode !== 'draft') return

        try {
          // Revisão já aplicada: o servidor serializa as escritas, então a
          // resposta que nos trouxe até `state.rev` já embute esta mudança.
          if (payload.rev <= state.rev) return

          if (payload.rev > state.rev + 1) {
            // Buraco de revisão: busca as ops que faltam em vez de baixar o
            // documento inteiro, que descartaria a edição local pendente.
            if (await catchUpMissedOps(projectId, state.rev)) return
            const draft = await draftApi.get(projectId)
            useDraftStore.getState().hydrateRealtimeDraft(draft)
            return
          }

          state.applyRealtimeOps(payload.rev, payload.ops as never, payload.actor ?? null)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[realtime] failed to apply remote ops', error)
        }
        return
      }

      if (payload.type === 'presence.leave') {
        usePresenceStore.getState().removeUser(payload.presence.userId)
        return
      }

      usePresenceStore.getState().upsertUser(payload.presence)
    }

    socket.onerror = (event) => {
      // Firefox can surface connection failures here before onclose.
      // eslint-disable-next-line no-console
      console.error('[realtime] websocket error', event)
    }

    socket.onclose = () => {
      if (heartbeatTimer) window.clearInterval(heartbeatTimer)
      heartbeatTimer = null
      usePresenceStore.getState().setStatus(closed ? 'idle' : 'disconnected')
      if (closed) return
      const delay = Math.min(1000 * 2 ** attempt, 8000)
      attempt += 1
      reconnectTimer = window.setTimeout(open, delay)
    }
  }

  const sendPresence = () => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return
    const selectedTableId = useSelectionTableId()
    socket.send(
      JSON.stringify({
        type: 'presence.heartbeat',
        editing: selectedTableId ? { tableId: selectedTableId } : null,
      }),
    )
  }

  open()

  return {
    sendPresence,
    close() {
      closed = true
      usePresenceStore.getState().clear()
      if (reconnectTimer) window.clearTimeout(reconnectTimer)
      if (heartbeatTimer) window.clearInterval(heartbeatTimer)
      socket?.close()
    },
  }
}

function useSelectionTableId() {
  return useSelectionStoreRef()
}

function useSelectionStoreRef() {
  return useSelectionStore.getState().selectedTableId
}
